<script lang="ts">
	import { create, fromJson, type JsonObject } from "@bufbuild/protobuf";
	import { Hr } from "flowbite-svelte";
	import { onDestroy } from "svelte";
	import { get, type Writable } from "svelte/store";
	import { ConversionMetaDataSchema, type ConversionMetaData } from "#gen/bundles/v1/events_pb.js";
	import { PolicySchema, type Policy } from "#gen/legal/v1/legal_pb.js";
	import type { AssetLoad } from "#lib/api/bundles.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import HtmlViewer from "#lib/components/content-editors/HtmlViewer.svelte";
	import { browser } from "$app/env";

	interface Props {
		asset: AssetLoad;
		metadata?: ConversionMetaData;
		onCancel?: (asset: AssetLoad, metadata: ConversionMetaData) => void;
	}

	const { asset, metadata, onCancel }: Props = $props();
	const policy = $derived(loadPolicy(asset?.asPolicy(), asset?.data()));

	onDestroy(() => {
		if (browser) {
			onCancel?.(asset, metadata ?? create(ConversionMetaDataSchema, {}));
		}
	});

	function loadPolicy(policy: Writable<Policy> | undefined, data: Writable<JsonObject | undefined>): Policy | undefined {
		if (policy) {
			return get(policy);
		}
		const json = get(data);
		return json ? fromJson(PolicySchema, json, { ignoreUnknownFields: true }) : undefined;
	}

	function handleLoad(e: Event) {
		const img = e.target as HTMLImageElement;
		img.classList.remove("opacity-0");
	}

	function handleError(e: Event) {
		const img = e.target as HTMLImageElement;
		img.src = "/scan-to-url-preview.png";
	}
</script>

{#snippet policyViewer(policyToView: Policy | undefined)}
	{#if policyToView?.content?.content.case === "text"}
		<div class="p-4 wrap-break-word">
			<HtmlViewer content={policyToView?.content?.content.value ?? ""}></HtmlViewer>
		</div>
	{:else if policyToView?.content?.content.case === "url"}
		<div class="flex items-center gap-2 px-4 pt-4">
			<Icon class="h-6 w-6 flex-none" name="clipboard-document-check" />
			<P weight="semibold" whitespace="nowrap" class="truncate font-sans">
				{policyToView?.content?.content.value}
			</P>
		</div>
		<Hr hrClass="mt-4" />
		<img class="w-full overflow-hidden object-cover object-top opacity-0 transition-opacity duration-200" onload={handleLoad} alt="Preview of external destination experience" src={`/api/screenshot?url=${encodeURIComponent(policyToView?.content?.content.value)}`} onerror={handleError} />
	{/if}
{/snippet}

<div>
	{#if policy}
		{@render policyViewer(policy)}
	{/if}
</div>
